/**
 * Leave Request Service - API client for staff leave applications and
 * balance ledger. Approving a request feeds real StaffAttendance(excused)
 * rows via the backend, same as staffAttendanceService feeds payroll.
 */
import api from '../utils/api';

const leaveRequestService = {
  // ---- Requests ----

  create: async (data) => {
    try {
      return await api.post('/api/leave-requests', data);
    } catch (error) {
      console.error('Error creating leave request:', error);
      throw error;
    }
  },

  getMy: async (status = null) => {
    try {
      const params = {};
      if (status) params.status = status;
      return await api.get('/api/leave-requests/my', { params });
    } catch (error) {
      console.error('Error fetching my leave requests:', error);
      throw error;
    }
  },

  list: async (filters = {}) => {
    try {
      return await api.get('/api/leave-requests', { params: filters });
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      throw error;
    }
  },

  get: async (requestId) => {
    try {
      return await api.get(`/api/leave-requests/${requestId}`);
    } catch (error) {
      console.error('Error fetching leave request:', error);
      throw error;
    }
  },

  approve: async (requestId) => {
    try {
      return await api.post(`/api/leave-requests/${requestId}/approve`, {});
    } catch (error) {
      console.error('Error approving leave request:', error);
      throw error;
    }
  },

  reject: async (requestId, rejectionReason = '') => {
    try {
      return await api.post(`/api/leave-requests/${requestId}/reject`, { rejection_reason: rejectionReason });
    } catch (error) {
      console.error('Error rejecting leave request:', error);
      throw error;
    }
  },

  cancel: async (requestId) => {
    try {
      return await api.post(`/api/leave-requests/${requestId}/cancel`, {});
    } catch (error) {
      console.error('Error cancelling leave request:', error);
      throw error;
    }
  },

  revoke: async (requestId, revokeReason = '') => {
    try {
      return await api.post(`/api/leave-requests/${requestId}/revoke`, { revoke_reason: revokeReason });
    } catch (error) {
      console.error('Error revoking leave request:', error);
      throw error;
    }
  },

  // ---- Manager approval ----

  managerApprove: async (requestId) => {
    try {
      return await api.post(`/api/leave-requests/${requestId}/manager-approve`, {});
    } catch (error) {
      console.error('Error manager-approving leave request:', error);
      throw error;
    }
  },

  managerReject: async (requestId, rejectionReason = '') => {
    try {
      return await api.post(`/api/leave-requests/${requestId}/manager-reject`, { rejection_reason: rejectionReason });
    } catch (error) {
      console.error('Error manager-rejecting leave request:', error);
      throw error;
    }
  },

  getPendingMyApproval: async () => {
    try {
      return await api.get('/api/leave-requests/pending-my-approval');
    } catch (error) {
      console.error('Error fetching requests pending my approval:', error);
      throw error;
    }
  },

  // ---- Calendar ----

  getCalendar: async (startDate, endDate) => {
    try {
      return await api.get('/api/leave-requests/calendar', { params: { start_date: startDate, end_date: endDate } });
    } catch (error) {
      console.error('Error fetching leave calendar:', error);
      throw error;
    }
  },

  // ---- Balances ----

  getMyBalances: async (year = null) => {
    try {
      const params = {};
      if (year) params.year = year;
      return await api.get('/api/leave-balances/my', { params });
    } catch (error) {
      console.error('Error fetching my leave balances:', error);
      throw error;
    }
  },

  listBalances: async (staffId = null, year = null) => {
    try {
      const params = {};
      if (staffId) params.staff_id = staffId;
      if (year) params.year = year;
      return await api.get('/api/leave-balances', { params });
    } catch (error) {
      console.error('Error fetching leave balances:', error);
      throw error;
    }
  },

  seedBalances: async (year, staffIds = null) => {
    try {
      return await api.post('/api/leave-balances/seed', { year, staff_ids: staffIds });
    } catch (error) {
      console.error('Error seeding leave balances:', error);
      throw error;
    }
  },

  updateBalance: async (balanceId, entitlementDays) => {
    try {
      return await api.put(`/api/leave-balances/${balanceId}`, { entitlement_days: entitlementDays });
    } catch (error) {
      console.error('Error updating leave balance:', error);
      throw error;
    }
  },

  // ---- Substitute coverage ----

  getCoverageNeeds: async (requestId) => {
    try {
      return await api.get(`/api/leave-requests/${requestId}/coverage`);
    } catch (error) {
      console.error('Error fetching coverage needs:', error);
      throw error;
    }
  },

  assignSubstitute: async (requestId, timetableEntryId, substituteTeacherId) => {
    try {
      return await api.post(`/api/leave-requests/${requestId}/coverage`, {
        timetable_entry_id: timetableEntryId,
        substitute_teacher_id: substituteTeacherId,
      });
    } catch (error) {
      console.error('Error assigning substitute:', error);
      throw error;
    }
  },

  bulkAssignSubstitute: async (requestId, substituteTeacherId) => {
    try {
      return await api.post(`/api/leave-requests/${requestId}/coverage/bulk`, {
        substitute_teacher_id: substituteTeacherId,
      });
    } catch (error) {
      console.error('Error bulk-assigning substitute:', error);
      throw error;
    }
  },

  removeSubstituteAssignment: async (requestId, timetableEntryId) => {
    try {
      return await api.delete(`/api/leave-requests/${requestId}/coverage/${timetableEntryId}`);
    } catch (error) {
      console.error('Error removing substitute assignment:', error);
      throw error;
    }
  },
};

export default leaveRequestService;
